import {Helmet} from 'react-helmet-async';
import {Redirect, Route, Switch} from 'react-router-dom';
import {NotAuthorizedError} from '@core/components/AppError';
import {FetchedUser} from '@core/graphql-api';
import {createOrgPath, isOrgSellSide} from '@core/lib/organizations';
import Loading from '@core/ui/Loading';
import Layout from '@analytics/components/Layout';
import {useOrganizationQuery} from '@analytics/graphql-api';
import {AdvisorIndex} from '../unified/advisor';
import {CampaignsIndex} from '../unified/campaigns';
import {ManageIndex} from '../unified/manage';
import {MeasureIndex} from '../unified/measure';
import {PodcastsIndex} from '../unified/podcasts';
import {ResearchIndex} from '../unified/research';
import {SellSideHome} from './SellSideHome';

type Props = {
  user: FetchedUser;
  match: {
    params: {slug: string};
  };
};

export function SellSideIndex({
  user,
  match: {
    params: {slug},
  },
}: Props) {
  const {data, loading, error} = useOrganizationQuery({
    variables: {slug},
  });

  const organization = data?.me?.organization;

  if (loading) {
    return <Loading fixed />;
  }

  if (error || !organization) {
    return <NotAuthorizedError user={user} />;
  }

  if (!isOrgSellSide(organization)) {
    return <Redirect to={createOrgPath(organization)} />;
  }

  return (
    <>
      <Helmet>
        <title>{organization.name} | Ad Analytics</title>
      </Helmet>
      <Layout user={user} organization={organization}>
        <Switch>
          {[
            {
              exact: true,
              path: '/s/:slug',
              Component: SellSideHome,
            },
            {path: '/s/:slug/podcasts', Component: PodcastsIndex},
            {path: '/s/:slug/campaigns', Component: CampaignsIndex},
            {path: '/s/:slug/research', Component: ResearchIndex},
            {path: '/s/:slug/advisor', Component: AdvisorIndex},
            {path: '/s/:slug/measure', Component: MeasureIndex},
            {path: '/s/:slug/manage', Component: ManageIndex},
          ].map(({exact = false, path, Component}) => (
            <Route
              key={path}
              exact={exact}
              path={path}
              render={(routeProps) => (
                <Component
                  user={user}
                  organization={organization}
                  {...routeProps}
                />
              )}
            />
          ))}
        </Switch>
      </Layout>
    </>
  );
}
